import { allLevels } from '../scenarios';
import { sprint42 } from '../scenarios/sprint42';
import type { PartnerId } from '../game/partners';
import { partnerById, partnerForTool, toolById } from '../game/partners';
import { BrandHeader } from './BrandHeader';
import { RTNav } from './RTNav';

interface Props {
  onBack: () => void;
}

/**
 * Trademark and source attribution.
 *
 * The game is built on other companies' interfaces, so it says so plainly and
 * in one place, and every level lists what it depicts. Two things this page has
 * to do: name the products so a player knows what they are looking at, and
 * disclaim any suggestion that the vendors were involved.
 *
 * EVERYTHING HERE IS GENERATED from the levels that exist and the tool
 * registry. Nothing is written by hand, including the trademark lines. That is
 * deliberate: this is the page most likely to quietly go stale, it is the page
 * where being stale matters most, and the failure would be silent. A vendor
 * added to a level appears here, with their own trademark line, in the same
 * change — or not at all.
 *
 * It reads allLevels() rather than the compiled LEVELS so that a level an
 * admin adds at runtime brings its vendor's trademark notice with it. A new
 * vendor reaching players with no attribution is exactly the silent failure
 * this page exists to prevent.
 */
export function AttributionNotice({ onBack }: Props) {
  const everything = [
    ...allLevels().map((l) => ({ title: l.title, source: l.source })),
    { title: sprint42.title, source: sprint42.source },
  ];

  // Vendors actually depicted, in the order they first appear.
  const partnerIds: PartnerId[] = [];
  for (const entry of everything) {
    const id = partnerForTool(entry.source.toolId).id;
    if (!partnerIds.includes(id)) partnerIds.push(id);
  }

  // The distinct products depicted, grouped by who owns them.
  const productsByPartner = new Map<PartnerId, string[]>();
  for (const entry of everything) {
    const tool = toolById(entry.source.toolId);
    const id = partnerForTool(entry.source.toolId).id;
    const list = productsByPartner.get(id) ?? [];
    if (!list.includes(tool.name)) list.push(tool.name);
    productsByPartner.set(id, list);
  }

  const vendorNames = partnerIds.map((id) => partnerById(id).name);
  const recreations = everything.filter((e) => e.source.kind === 'recreation');

  return (
    <div className="panel">
      <RTNav />
      <div className="panel__inner">
        <BrandHeader />
        <p className="case-eyebrow">Attribution</p>
        <h1>What these puzzles are based on</h1>
        <p className="lede">
          Every puzzle in this game depicts a real software product. Here is
          exactly which, and who owns it.
        </p>

        <h2>Not affiliated with the vendors</h2>
        <p>
          DevTool Detective is made by <b>ReleaseTEAM</b>. It is{' '}
          <b>
            not affiliated with, endorsed by, sponsored by, or produced in
            association with
          </b>{' '}
          {joinNames(vendorNames)}, or any other vendor whose product appears
          here.
        </p>

        <h2>Trademarks</h2>
        <ul className="rules">
          {partnerIds.map((id) => {
            const partner = partnerById(id);
            const products = productsByPartner.get(id) ?? [];
            return (
              <li key={id}>
                <b style={{ color: partner.accent }}>{partner.name}</b>
                {products.length > 0 && <> — {joinNames(products)}</>}
                <br />
                {joinNames([partner.name, ...products])} are trademarks or
                registered trademarks of {partner.legalName}. No claim is made to
                any of them.
              </li>
            );
          })}
        </ul>
        <p className="fineprint">
          Product names are used only to identify the software being examined —
          that is the whole point of the exercise.
        </p>

        <h2>Where the material comes from</h2>
        <ul className="rules">
          <li>
            <b>Screenshots are of ReleaseTEAM's own test instances.</b> They show
            configuration deliberately set up to be wrong, for teaching. They are
            not customer systems, and no customer data appears anywhere in this
            game.
          </li>
          <li>
            <b>The red arrows and every judgement are ours.</b> Where a puzzle
            says something is misconfigured, that is ReleaseTEAM's opinion about
            how a team should use the product — not a defect in the product, and
            not a statement by its vendor.
          </li>
          {recreations.length > 0 && (
            <li>
              <b>
                {recreations.length === 1
                  ? 'One level is a recreation, not a screenshot.'
                  : `${recreations.length} levels are recreations, not screenshots.`}
              </b>{' '}
              {recreations.length === 1 ? 'It is' : 'They are'} original work
              imitating the layout and conventions of the real interface;{' '}
              {recreations.length === 1 ? 'it contains' : 'they contain'} no
              vendor code, assets or images.{' '}
              {recreations.length === 1 ? 'It is' : 'They are'} marked as such
              below.
            </li>
          )}
        </ul>

        <h2>Level by level</h2>
        <div className="attribution-list">
          {everything.map((entry) => {
            const tool = toolById(entry.source.toolId);
            const partner = partnerForTool(entry.source.toolId);
            return (
              <div key={entry.title} className="attribution-row">
                <div className="attribution-row__title">{entry.title}</div>
                <div className="attribution-row__meta">
                  <span style={{ color: partner.accent }}>{tool.name}</span> ·{' '}
                  {entry.source.screen}
                  <span
                    className={`attribution-kind attribution-kind--${entry.source.kind}`}
                  >
                    {entry.source.kind === 'screenshot' ? 'screenshot' : 'recreation'}
                  </span>
                </div>
                {entry.source.note && (
                  <div className="attribution-row__note">{entry.source.note}</div>
                )}
              </div>
            );
          })}
        </div>

        <h2>If you own one of these products</h2>
        <p>
          If anything here misrepresents your product, or you would like the
          attribution worded differently, email{' '}
          <a href="mailto:info@releaseteam.com">info@releaseteam.com</a> and we
          will change it.
        </p>

        <button type="button" className="btn btn--wide" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}

/** "a, b and c" — a list a person would read out, not an array join. */
function joinNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  const head = names.slice(0, -1).join(', ');
  return `${head} and ${names[names.length - 1]}`;
}
